/**
 * UserDashboardPage
 * Viewer for non-ED roles: ES, CS, PE, PS, EA, CA.
 *
 * Shows dashboard items as cards (same UI as admin dashboard config).
 * Clicking a card loads the Metabase iframe for that dashboard.
 * Back button returns to the card list view.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Loader2, LayoutDashboard, RefreshCw, AlertCircle, Star, BarChart2, BarChart3, SlidersHorizontal, CalendarDays, ArrowLeft, ChevronRight, ChevronLeft, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { enrollerAdminService } from '@/services/ea/enrollerAdminService';
import { normaliseDashboardItem } from '@/hooks/dashboardConfig/useDashboardConfig';
import type { NormalisedDashboardItem } from '@/types/dashboardConfig';

// ─── Constants ────────────────────────────────────────────────────────────────

const REFRESH_BUFFER_MS = 60_000;
const IS_DEV = import.meta.env.DEV;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Dashboard Card (same UI as admin DashboardConfigList) ────────────────────

function DashboardCard({
  item,
  onClick,
}: {
  item: NormalisedDashboardItem;
  onClick: (item: NormalisedDashboardItem) => void;
}) {
  return (
    <Card
      className="border border-border hover:shadow-md hover:border-primary/30 hover:bg-accent/50 transition-all cursor-pointer group"
      onClick={() => onClick(item)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="mt-0.5 rounded-md bg-accent p-1.5 shrink-0 group-hover:bg-primary/10 transition-colors">
              <LayoutDashboard className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0 space-y-1 flex-1">
              {/* Name + Default badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-foreground truncate">
                  {item.customName}
                </span>
                {item.isDefault && (
                  <Badge className="shrink-0 gap-1 text-[10px] py-0 px-1.5 bg-primary text-primary-foreground border-primary">
                    <Star className="h-2.5 w-2.5 fill-current" />
                    Default
                  </Badge>
                )}
              </div>

              {/* Dashboard source name */}
              <p className="text-xs text-muted-foreground truncate">
                {item.metabaseQuestionName}
              </p>

              {/* Meta row */}
              <div className="flex items-center gap-4 pt-1 flex-wrap">
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <BarChart2 className="h-3 w-3" />
                  {item.dashboardQuestions.length} questions
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <SlidersHorizontal className="h-3 w-3" />
                  {item.dashboardFilters.length} filters
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CalendarDays className="h-3 w-3" />
                  {formatDate(item.createdAt)}
                </span>
                {item.updatedDate && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <RefreshCw className="h-3 w-3" />
                    {formatDate(item.updatedDate)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center self-center shrink-0">
            <ChevronRight className="h-5 w-5 text-muted-foreground/50 group-hover:text-primary transition-colors" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const UserDashboardPage = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const loginUserId = user?.id ?? null;

  const userType = (user?.type ?? '').toUpperCase();
  const userEmail = (user?.email ?? '') || '';

  // ── Dashboard item list ────────────────────────────────────────────────────
  const [items, setItems] = useState<NormalisedDashboardItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // ── View mode: 'list' shows cards, 'iframe' shows the dashboard ────────────
  const [viewMode, setViewMode] = useState<'list' | 'iframe'>('list');
  const [selected, setSelected] = useState<NormalisedDashboardItem | null>(null);
  const [listCollapsed, setListCollapsed] = useState(false);

  // ── Iframe state ───────────────────────────────────────────────────────────
  const [iframeUrlState, setIframeUrlState] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);

  // ── AbortController ref ────────────────────────────────────────────────────
  const abortRef = useRef<AbortController | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const showIframeError = useCallback(
    (message: string) => {
      setIframeError(message);
      toast({
        title: 'Dashboard load failed',
        description: message,
        variant: 'destructive',
      });
    },
    [toast]
  );

  const applyIframeUrl = useCallback(
    (url: string, expiresAtEpochMs: number | undefined, reloadFn: () => void) => {
      setIframeUrlState((prev) => {
        if (prev === url) {
          setIframeKey((k) => k + 1);
        }
        return url;
      });
      clearRefreshTimer();
      if (expiresAtEpochMs) {
        const delay = expiresAtEpochMs - Date.now() - REFRESH_BUFFER_MS;
        if (delay > 0) {
          refreshTimerRef.current = setTimeout(reloadFn, delay);
        }
      }
    },
    [clearRefreshTimer]
  );

  // ── Page title ──────────────────────────────────────────────────────────────
  const pageLabel = userType === 'EA' ? 'My Dashboards' : 'Analytics';
  useEffect(() => {
    document.title = selected && viewMode === 'iframe' ? `${selected.customName} | Dashboard` : pageLabel;
  }, [selected, viewMode, pageLabel]);

  // ── Cleanup ─────────────────────────────────────────────────────────────────
  useEffect(
    () => () => {
      clearRefreshTimer();
      abortRef.current?.abort();
    },
    [clearRefreshTimer]
  );

  // ── Load iframe via token ──────────────────────────────────────────────────
  const loadIframeViaToken = useCallback(
    async (
      dashItem: NormalisedDashboardItem,
      controller: AbortController,
      reloadFn: () => void
    ) => {
      const resourceType = dashItem.resourceType ?? 'dashboard';
      const emailParam = userEmail || '';

      const response = await enrollerAdminService.getMetabaseToken({
        type: resourceType,
        id: dashItem.resourceId,
        user_email: emailParam,
        filters: dashItem.dashboardFilters ?? [],
        userContext: {
          userId: loginUserId ?? undefined,
          userEmail: userEmail,
          userFullName: user?.fullName ?? '',
        },
      });

      if (controller.signal.aborted) return false;

      if (response?.token) {
        const url = enrollerAdminService.buildMetabaseEmbedUrl(resourceType, response.token);
        applyIframeUrl(url, response.expires_at_epoch_ms, reloadFn);
        return true;
      }

      return false;
    },
    [userType, userEmail, loginUserId, user?.fullName, applyIframeUrl]
  );

  // ── Load items list ────────────────────────────────────────────────────────
  const loadItems = useCallback(
    async (role: string) => {
      if (!loginUserId || !token || !role) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const normalizedRole = role.toUpperCase();
      enrollerAdminService.updateContext({ userId: loginUserId, token });

      setItemsLoading(true);

      try {
        const rawItems = await enrollerAdminService
          .getDashboardItems(normalizedRole)
          .catch(() => []);

        if (controller.signal.aborted) return;

        const normalisedItems = (rawItems ?? []).map(normaliseDashboardItem);
        setItems(normalisedItems);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (IS_DEV) console.error('[UserDashboardPage] ❌ loadItems error:', err);
      } finally {
        if (!controller.signal.aborted) setItemsLoading(false);
      }
    },
    [loginUserId, token]
  );

  // ── On mount: load items list ──────────────────────────────────────────────
  useEffect(() => {
    if (!user || !token || !userType) return;
    setItems([]);
    setViewMode('list');
    setSelected(null);
    setIframeUrlState(null);
    setIframeError(null);
    loadItems(userType);
  }, [user, token, userType, loadItems]);

  // ── Auto-load default (or first) dashboard for non-EA roles ────────────────
  const isSplitView = userType !== 'EA';
  useEffect(() => {
    if (!isSplitView) return;
    if (itemsLoading || items.length === 0 || selected) return;
    const sorted = [...items].sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    handleCardClick(sorted[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSplitView, itemsLoading, items]);

  // ── Card click: load iframe for that item ──────────────────────────────────
  const handleCardClick = useCallback(
    async (dashItem: NormalisedDashboardItem) => {
      if (!loginUserId || !token) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      enrollerAdminService.updateContext({ userId: loginUserId, token });

      setSelected(dashItem);
      setViewMode('iframe');
      clearRefreshTimer();
      setIframeUrlState(null);
      setIframeError(null);
      setIframeLoading(true);

      try {
        const success = await loadIframeViaToken(dashItem, controller, () =>
          handleCardClick(dashItem)
        );

        if (controller.signal.aborted) return;

        if (!success) {
          showIframeError('No token returned from server.');
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        showIframeError(err instanceof Error ? err.message : 'Failed to load dashboard.');
      } finally {
        if (!controller.signal.aborted) setIframeLoading(false);
      }
    },
    [loginUserId, token, clearRefreshTimer, loadIframeViaToken, showIframeError]
  );

  // ── Back to list ───────────────────────────────────────────────────────────
  const handleBack = useCallback(() => {
    abortRef.current?.abort();
    clearRefreshTimer();
    setViewMode('list');
    setSelected(null);
    setIframeUrlState(null);
    setIframeError(null);
    setIframeLoading(false);
  }, [clearRefreshTimer]);

  // ── Refresh iframe ─────────────────────────────────────────────────────────
  const refreshIframe = useCallback(() => {
    if (selected) handleCardClick(selected);
  }, [selected, handleCardClick]);

  // ── Sort: default first, then by date ──────────────────────────────────────
  const sortedItems = [...items].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // ─── Render ──────────────────────────────────────────────────────────────────

  // SPLIT VIEW for non-EA roles: list on the left, iframe on the right
  if (isSplitView) {
    return (
      <div className="space-y-4 flex flex-col h-[calc(100vh-120px)] overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setListCollapsed((v) => !v)}
              title={listCollapsed ? 'Show dashboards list' : 'Hide dashboards list'}
              aria-label={listCollapsed ? 'Show dashboards list' : 'Hide dashboards list'}
            >
              {listCollapsed ? <Menu className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-foreground truncate">{pageLabel}</h1>
              {itemsLoading && (
                <p className="text-sm text-muted-foreground mt-0.5">Loading…</p>
              )}
            </div>
          </div>
          {selected && (iframeUrlState || iframeError) && !iframeLoading && (
            <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={refreshIframe}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          )}
        </div>

        {!itemsLoading && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground border border-border rounded-lg">
            <BarChart3 className="h-12 w-12 mb-3 opacity-25" />
            <p className="text-sm mt-1">No Analytics Dashboard has been assigned to you yet. Please contact your enroller admin.</p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 gap-4 flex-1 min-h-0 overflow-hidden ${listCollapsed ? 'lg:grid-cols-[72px_1fr]' : 'lg:grid-cols-[280px_1fr]'}`}>
            {/* Left: list */}
            {!listCollapsed ? (
              <div className="space-y-3 overflow-y-auto pr-1 pt-2 min-h-0">
              {sortedItems.map((item) => {
                const active = selected?.id === item.id;
                return (
                  <Card
                    key={item.id}
                    onClick={() => handleCardClick(item)}
                    className={`relative shadow-none hover:shadow-sm transition-all cursor-pointer group border ${
                      active
                        ? 'bg-primary/10 border-primary'
                        : 'bg-card border-border/60 hover:border-primary/40 hover:bg-accent/40'
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="min-w-0 flex-1 space-y-1">
                          {/* Title row */}
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="font-semibold text-sm text-foreground truncate flex-1">
                              {item.customName}
                            </span>
                            {item.isDefault && (
                              <Badge className="shrink-0 gap-0.5 text-[9px] py-0 px-1 h-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">
                                <Star className="h-2 w-2 fill-current" />
                                Default
                              </Badge>
                            )}
                          </div>

                          {item.metabaseQuestionName && item.metabaseQuestionName !== item.customName && (
                            <p className="text-xs text-muted-foreground truncate">
                              {item.metabaseQuestionName}
                            </p>
                          )}

                          {/* Meta row — wraps when needed */}
                          <div className="flex items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground/80 flex-wrap">
                            {item.dashboardQuestions.length > 0 && (
                              <span
                                className="flex items-center gap-1"
                                title={`${item.dashboardQuestions.length} question${item.dashboardQuestions.length === 1 ? '' : 's'}`}
                              >
                                <BarChart2 className="h-2.5 w-2.5" />
                                {item.dashboardQuestions.length}
                              </span>
                            )}
                            {item.dashboardQuestions.length > 0 && item.dashboardFilters.length > 0 && (
                              <span className="text-border">•</span>
                            )}
                            {item.dashboardFilters.length > 0 && (
                              <span
                                className="flex items-center gap-1"
                                title={`${item.dashboardFilters.length} filter${item.dashboardFilters.length === 1 ? '' : 's'}`}
                              >
                                <SlidersHorizontal className="h-2.5 w-2.5" />
                                {item.dashboardFilters.length}
                              </span>
                            )}
                            {item.createdAt && (
                              <>
                                {(item.dashboardQuestions.length > 0 || item.dashboardFilters.length > 0) && (
                                  <span className="text-border">•</span>
                                )}
                                <span
                                  className="flex items-center gap-1"
                                  title={`Created: ${formatDate(item.createdAt)}`}
                                >
                                  <CalendarDays className="h-2.5 w-2.5" />
                                  {formatDate(item.createdAt)}
                                </span>
                              </>
                            )}
                            {item.updatedDate && (
                              <>
                                <span className="text-border">•</span>
                                <span
                                  className="flex items-center gap-1"
                                  title={`Updated: ${formatDate(item.updatedDate)}`}
                                >
                                  <RefreshCw className="h-2.5 w-2.5" />
                                  {formatDate(item.updatedDate)}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Role chips - hidden for ES, CS, PE roles */}
                          {item.userTypes.length > 0 && !['ES', 'CS', 'PE'].includes(userType) && (
                            <div className="flex flex-wrap gap-1 pt-0.5">
                              {item.userTypes.map((role) => (
                                <Badge
                                  key={role}
                                  variant="secondary"
                                  className="text-[9px] px-1.5 py-0 h-4 font-medium"
                                >
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            ) : (
              <div className="min-h-0 overflow-y-auto pt-2">
                <Card className="border-border/70">
                  <CardContent className="p-2 flex flex-col items-center gap-2">
                    {sortedItems.map((item) => {
                      const isActive = selected?.id === item.id;
                      return (
                        <Button
                          key={item.id}
                          variant={isActive ? 'default' : 'ghost'}
                          className={`h-10 w-10 p-0 ${
                            isActive ? 'bg-primary text-primary-foreground' : 'border border-border'
                          }`}
                          title={item.customName}
                          onClick={() => handleCardClick(item)}
                        >
                          <BarChart2 className="h-4 w-4" />
                        </Button>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Right: iframe */}
            <div className="min-w-0 min-h-0 flex flex-col overflow-hidden pt-2">
              {!selected && !iframeLoading && (
                <div className="flex items-center justify-center py-24 border border-border rounded-lg text-muted-foreground text-sm">
                  Select a dashboard to view
                </div>
              )}

              {selected && (
                <div className="flex flex-col flex-1 min-h-0 gap-3">
                  {iframeLoading && (
                    <div className="flex items-center justify-center py-24 bg-muted/40 rounded-lg text-muted-foreground">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Loading dashboard…
                    </div>
                  )}

                  {iframeError && !iframeLoading && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="flex items-center justify-between gap-4">
                        <span>{iframeError}</span>
                        <Button size="sm" variant="outline" className="shrink-0" onClick={refreshIframe}>
                          Retry
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}

                  {iframeUrlState && !iframeLoading && !iframeError && (
                    <Card className="flex-1 min-h-0 flex flex-col">
                      <CardContent className="p-0 rounded-lg flex-1 min-h-0 overflow-auto">
                        <iframe
                          key={`${iframeUrlState}-${iframeKey}`}
                          src={iframeUrlState}
                          width="100%"
                          className="block w-full h-[calc(100vh-200px)] max-h-[calc(100vh-200px)] min-h-[600px]"
                          title={selected?.customName ?? 'Dashboard'}
                          allowFullScreen
                          scrolling="auto"
                        />
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // IFRAME VIEW (EA legacy)
  if (viewMode === 'iframe') {
    return (
      <div className="space-y-4">
        {/* Header with back button */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            {selected && (
              <>
                <h1 className="text-xl font-semibold text-foreground truncate">
                  {selected.customName}
                </h1>
                {selected.isDefault && (
                  <Badge className="shrink-0 gap-1 text-[10px] py-0 px-1.5 bg-primary text-primary-foreground border-primary">
                    <Star className="h-2.5 w-2.5 fill-current" />
                    Default
                  </Badge>
                )}
              </>
            )}
          </div>

          {(iframeUrlState || iframeError) && !iframeLoading && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={refreshIframe}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          )}
        </div>

        {/* Iframe loading */}
        {iframeLoading && (
          <div className="flex items-center justify-center py-24 bg-muted/40 rounded-lg text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading dashboard…
          </div>
        )}

        {/* Error state */}
        {iframeError && !iframeLoading && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between gap-4">
              <span>{iframeError}</span>
              <Button size="sm" variant="outline" className="shrink-0" onClick={refreshIframe}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Iframe */}
        {iframeUrlState && !iframeLoading && !iframeError && (
          <Card>
            <CardContent className="p-0 rounded-lg overflow-auto">
              <iframe
                key={`${iframeUrlState}-${iframeKey}`}
                src={iframeUrlState}
                width="100%"
                className="block w-full h-[calc(100vh-160px)] max-h-[calc(100vh-160px)] min-h-[600px]"
                title={selected?.customName ?? 'Dashboard'}
                allowFullScreen
                scrolling="auto"
              />
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // LIST VIEW (cards — same UI as admin dashboard config)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{pageLabel}</h1>
          {itemsLoading && (
            <p className="text-sm text-muted-foreground mt-0.5">Loading…</p>
          )}
        </div>
      </div>

      {/* Loading */}
      {itemsLoading && (
        <div className="flex items-center gap-2 text-muted-foreground py-6 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading dashboards…
        </div>
      )}

      {/* Empty state */}
      {!itemsLoading && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
          <BarChart3 className="h-12 w-12 mb-3 opacity-25" />
          <p className="text-sm mt-1">No Analytics Dashboard has been assigned to you yet. Please contact your enroller admin.</p>
        </div>
      )}

      {/* Cards grid */}
      {!itemsLoading && items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {sortedItems.map((item) => (
            <DashboardCard key={item.id} item={item} onClick={handleCardClick} />
          ))}
        </div>
      )}
    </div>
  );
};

export { UserDashboardPage };
export default UserDashboardPage;
